import React, { useState } from 'react';
import { format } from 'date-fns';
import type { Payslip } from './payrollTypes';
import { formatINR } from '@/shared/utils/currency';

interface PrintablePayslipProps {
  payslip: Payslip;
}

function numberToWordsINR(amount: number): string {
  const num = Math.round(Number(amount) || 0);
  if (num <= 0) return 'Zero Rupees Only';

  const units = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
    'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  function convertLessThanOneThousand(n: number): string {
    let current = '';
    if (n >= 100) {
      current += units[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n >= 20) {
      current += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    }
    if (n > 0) {
      current += units[n] + ' ';
    }
    return current.trim();
  }

  let crore = Math.floor(num / 10000000);
  let remainder = num % 10000000;
  let lakh = Math.floor(remainder / 100000);
  remainder = remainder % 100000;
  let thousand = Math.floor(remainder / 1000);
  let rest = remainder % 1000;

  const parts: string[] = [];
  if (crore > 0) parts.push(convertLessThanOneThousand(crore) + ' Crore');
  if (lakh > 0) parts.push(convertLessThanOneThousand(lakh) + ' Lakh');
  if (thousand > 0) parts.push(convertLessThanOneThousand(thousand) + ' Thousand');
  if (rest > 0) parts.push(convertLessThanOneThousand(rest));

  return (parts.join(' ') + ' Rupees Only').trim();
}

export const PrintablePayslip: React.FC<PrintablePayslipProps> = ({ payslip }) => {
  const [generatedFallback] = useState(() => Date.now());
  const totalEarnings = payslip.earnings?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) || 0;
  const totalDeductions = payslip.deductions?.reduce((sum, item) => sum + (Number(item.amount) || 0), 0) || 0;
  const netSalary = payslip.net_salary !== undefined ? payslip.net_salary : (totalEarnings - totalDeductions);

  const maxRows = Math.max(payslip.earnings?.length || 0, payslip.deductions?.length || 0);
  const rows = Array.from({ length: maxRows }, (_, i) => ({
    earning: payslip.earnings?.[i],
    deduction: payslip.deductions?.[i],
  }));

  const userPhone = payslip.phone || payslip.user?.phone || payslip.user?.mobile || 'N/A';
  const userEmpId = payslip.employee_id_code || payslip.user?.employee_id || (payslip.user?.id ? payslip.user.id.substring(0, 8).toUpperCase() : 'N/A');
  const userDoj = payslip.doj || (payslip.user?.date_of_joining ? format(new Date(payslip.user.date_of_joining), 'dd MMM yyyy') : (payslip.user?.joining_date ? format(new Date(payslip.user.joining_date), 'dd MMM yyyy') : 'N/A'));
  const bankName = payslip.bank_name || payslip.user?.bank_name || 'N/A';
  const bankAccount = payslip.bank_account || payslip.user?.bank_account_no || 'N/A';
  const ifscCode = payslip.ifsc || payslip.user?.bank_ifsc || 'N/A';

  return (
    <div className="p-8 bg-white text-slate-900 min-h-[600px] font-sans rounded-md shadow-sm border border-slate-200" id={`payslip-print-${payslip.id}`}>
      {/* RBM Header */}
      <div className="flex items-center justify-between border-b-2 border-red-600 pb-4 mb-6">
        <div className="flex items-center gap-4">
          <img
            src="/rbm-logo.png"
            alt="RBM Oil Corporation Logo"
            className="h-16 w-auto object-contain"
            onError={(e) => {
              (e.target as HTMLElement).style.display = 'none';
            }}
          />
          <div>
            <h1 className="text-xl font-bold tracking-tight text-red-700 uppercase">RBM OIL CORPORATION</h1>
            <p className="text-xs text-slate-600 font-medium leading-relaxed">
              Sr. No. 42 Rasikwadi, Jambe Road, Pune, Maharashtra 411033<br />
              GSTIN: 27AYJPD3083D1ZN | Mob: +91 9146 588 595 | Email: sales@rbmoilcorporation.in
            </p>
          </div>
        </div>
        <div className="text-right">
          <span className="inline-block px-3 py-1 bg-red-50 text-red-700 border border-red-200 rounded text-xs font-semibold uppercase tracking-wider">
            SALARY SLIP
          </span>
          <p className="text-sm font-semibold text-slate-800 mt-1">{payslip.month_year}</p>
        </div>
      </div>

      {/* Employee Details Strip */}
      <div className="bg-slate-50 border border-slate-200 rounded p-4 mb-6 grid grid-cols-2 gap-4 text-sm">
        <div className="space-y-1.5">
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Employee Name:</span> <span className="font-semibold text-slate-900">{payslip.user?.display_name || 'N/A'}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Employee ID:</span> <span className="font-mono text-slate-800">{userEmpId}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Department:</span> <span className="text-slate-800">{payslip.user?.department || 'Operations'}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Designation:</span> <span className="text-slate-800">{payslip.user?.position || 'Associate'}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Mobile No:</span> <span className="text-slate-800 font-mono">{userPhone}</span></div>
        </div>
        <div className="space-y-1.5">
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Date of Joining:</span> <span className="text-slate-800">{userDoj}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Bank Name:</span> <span className="text-slate-800">{bankName}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Account No:</span> <span className="text-slate-800 font-mono">{bankAccount}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">IFSC Code:</span> <span className="text-slate-800 font-mono">{ifscCode}</span></div>
          <div className="flex"><span className="w-36 text-slate-500 font-medium">Generated Date:</span> <span className="text-slate-800">{format(new Date(payslip.created_at || generatedFallback), 'dd MMM yyyy')}</span></div>
        </div>
      </div>

      {/* Salary Breakdown Table */}
      <div className="border border-slate-300 rounded overflow-hidden mb-6">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-slate-100 text-slate-700 border-b border-slate-300 font-semibold">
              <th className="py-2.5 px-4 text-left border-r border-slate-300 w-1/4">Earnings</th>
              <th className="py-2.5 px-4 text-right border-r border-slate-300 w-1/4">Amount (₹)</th>
              <th className="py-2.5 px-4 text-left border-r border-slate-300 w-1/4">Deductions</th>
              <th className="py-2.5 px-4 text-right w-1/4">Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50">
                <td className="py-2 px-4 text-slate-800 border-r border-slate-200">{row.earning?.label || ''}</td>
                <td className="py-2 px-4 text-right font-mono text-slate-800 border-r border-slate-200">
                  {row.earning ? formatINR(row.earning.amount) : ''}
                </td>
                <td className="py-2 px-4 text-slate-800 border-r border-slate-200">{row.deduction?.label || ''}</td>
                <td className="py-2 px-4 text-right font-mono text-slate-800">
                  {row.deduction ? formatINR(row.deduction.amount) : ''}
                </td>
              </tr>
            ))}
            {/* Total Row */}
            <tr className="bg-slate-100 font-bold border-t-2 border-slate-300 text-slate-900">
              <td className="py-2.5 px-4 border-r border-slate-300">Total Gross Salary</td>
              <td className="py-2.5 px-4 text-right font-mono text-emerald-700 border-r border-slate-300">{formatINR(totalEarnings)}</td>
              <td className="py-2.5 px-4 border-r border-slate-300">Total Deductions</td>
              <td className="py-2.5 px-4 text-right font-mono text-red-700">{formatINR(totalDeductions)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Net Salary Payable & Amount in Words */}
      <div className="bg-red-50 border-2 border-red-200 rounded p-4 mb-8">
        <div className="flex justify-between items-center mb-1">
          <span className="text-base font-bold text-red-900">Net / In-Hand Salary Payable</span>
          <span className="text-2xl font-black font-mono text-red-700">{formatINR(netSalary)}</span>
        </div>
        <div className="text-xs text-slate-700 italic border-t border-red-200/60 pt-1.5 mt-1">
          <span className="font-semibold text-slate-800 not-italic">Amount in Words: </span>
          {numberToWordsINR(netSalary)}
        </div>
      </div>

      {/* Signatures */}
      <div className="pt-8 flex justify-between px-8 text-xs text-slate-600">
        <div className="text-center">
          <div className="w-52 border-t-2 border-slate-400 mb-2"></div>
          <p className="font-semibold text-slate-800">Authorised Signatory</p>
          <p>For RBM OIL CORPORATION</p>
        </div>
        <div className="text-center">
          <div className="w-52 border-t-2 border-slate-400 mb-2"></div>
          <p className="font-semibold text-slate-800">Employee Signature</p>
          <p>{payslip.user?.display_name || 'Signature'}</p>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{__html: `
        @media print {
          body * { visibility: hidden; }
          #payslip-print-${payslip.id}, #payslip-print-${payslip.id} * { visibility: visible; }
          #payslip-print-${payslip.id} { position: absolute; left: 0; top: 0; width: 100%; height: auto; padding: 20px; margin: 0; box-sizing: border-box; }
        }
      `}} />
    </div>
  );
};
